import { type CSSProperties, type ReactNode } from 'react'
import styles from './PageLayout.module.css'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { usePageTopOffset } from '@/contexts/page-top-offset-context'
import { isEmptyNode } from '@/utils/react-util'

interface PageProps {
  children?: ReactNode
  testId?: string
}

/**
 * Page
 */
export function Page({ children, testId }: PageProps) {
  const pageTopOffset = usePageTopOffset()
  return (
    <div
      className={styles.page}
      style={{ '--page-top-offset': `${pageTopOffset}px` } as CSSProperties}
      data-test={testId}
    >
      {children}
    </div>
  )
}

export interface PageLayoutProps {
  children?: ReactNode

  /**
   * Rendered at the top of the page, spanning the entire width of the layout
   * above the columns.
   */
  top?: ReactNode

  /**
   * Rendered at the top of the secondary column on spacious layouts,
   * and at the top of the page on compact layouts, but below the `callToAction`.
   */
  asideTop?: ReactNode

  /**
   * Rendered at the top of the primary column on spacious layouts,
   * and at the top of the page on compact layouts.
   */
  callToAction?: ReactNode

  /**
   * Rendered at the bottom of the secondary column on spacious layouts,
   * and at the bottom of the page on compact layouts.
   */
  asideBottom?: ReactNode

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Page layout
 */
export function PageLayout({
  top,
  asideTop,
  asideBottom,
  callToAction,
  children,
  testId,
}: PageLayoutProps) {
  const isSpacious = useIsSpacious()
  const isColumnLayout =
    isSpacious && (!isEmptyNode(asideTop) || !isEmptyNode(asideBottom))
  return (
    <div className={styles.layout} data-test={testId}>
      <div className="max-w-content mx-auto">
        <div className={styles.top}>{top}</div>
        {isColumnLayout ? (
          <div className={styles.columns}>
            <main className={styles.primaryColumn}>
              {callToAction}
              {children}
            </main>
            <aside className={styles.secondaryColumn}>
              {asideTop}
              {asideBottom}
            </aside>
          </div>
        ) : (
          <main>
            {callToAction}
            {asideTop}
            {children}
            {asideBottom}
          </main>
        )}
      </div>
    </div>
  )
}

interface PageSectionProps {
  children?: ReactNode
  testId?: string
}

/**
 * Page Section
 */
export function PageSection({ children, testId }: PageSectionProps) {
  return (
    <div className={styles.section} data-test={testId}>
      {children}
    </div>
  )
}
